import { assertSafeInteger } from '@kenocyber/shared';

/**
 * **A composição canónica da velocidade de movimento de um casco, e a única que
 * existe.**
 *
 * ## O problema que este ficheiro resolve
 *
 * Há dois sítios que deslocam uma nave por vontade própria - a caça e o duelo -
 * e eles não partilhavam nada. O duelo multiplica um passo por tique pela
 * velocidade do casco em basis points; a caça faz a nave vaguear por uma função
 * do tique e da semente, sem número de velocidade nenhum. No dia em que uma joia
 * passa a somar uma percentagem de movimento, essa diferença deixa de ser
 * inofensiva: a mesma peça teria de ser somada em dois sítios que ninguém obriga
 * a concordar, e o primeiro a divergir seria aquele em que ninguém tocou.
 *
 * Então há UMA lei, aqui, e as duas simulações atravessam para ela.
 *
 * ## A lei, e ela é uma linha
 *
 *     velocidadeBps = velocidadeBaseBps + somaDosBonusBps
 *
 * `velocidadeBaseBps` é a velocidade própria do casco em basis points, onde
 * {@link MOVE_SPEED_BPS_ONE} é a nave de referência - no duelo é
 * `profile.speedBps`, na caça é a referência exata, porque a nave da caça não
 * tem hoje bónus próprio nenhum. `somaDosBonusBps` é a SOMA de todas as
 * percentagens de movimento, somadas primeiro e aplicadas **uma única vez** por
 * quem gasta o resultado.
 *
 * ## Somadas, e nunca multiplicadas em sequência
 *
 * É a mesma lei aditiva que `damage/exact.ts` impõe do lado do tiro e que
 * `hull/max-health.ts` impõe do lado do casco, e a razão é a mesma: multiplicar
 * em cadeia faz cada fonte nova valer mais por existirem outras, que é a
 * realimentação que o contrato V28D.2 proíbe.
 *
 * A prova aritmética do gate V28G.T é exatamente esta diferença. Com um casco de
 * `+20 %` próprios e uma joia de movimento ao nível 30 (`+39 %`):
 *
 *     10 000 + 2 000 + 3 900 = 13 900   ->  1,39x  ... 1,59x da base   <- correto
 *     12 000 x 13 900 / 10 000 = 16 680 ->  1,668x da base             <- proibido
 *
 * ## O que esta lei NÃO governa
 *
 * Qualquer deslocamento que a nave não pediu. Um empurrão de bomba, um
 * teleporte, uma correção da autoridade, um reposicionamento de spawn ou de
 * reconexão e um knockback são movimentos que as REGRAS impõem, e nenhum deles
 * passa por aqui. O que sai desta função multiplica o passo do JOGADOR, e é essa
 * separação - e não uma disciplina de quem escreve - que faz uma nave empurrada
 * não recuperar o controlo mais cedo por ter uma passiva de movimento.
 *
 * E não governa nada que se desenhe. Não há aqui uma cadência, uma velocidade de
 * projétil, uma órbita, uma animação nem uma textura: o que sai é um número de
 * basis points, e quem o gasta gasta-o num passo.
 */

/**
 * A nave de referência: `10 000` basis points é velocidade inalterada.
 *
 * A mesma constante que `PVP_BASE_SPEED_BPS` já era do lado do duelo, nomeada
 * aqui porque a caça passa a precisar dela e importar uma constante de `pvp/`
 * para dentro de `hunt/` ligaria os dois modos por uma dependência que não
 * existe. Esta folha não importa nenhum dos dois, e os dois importam-na.
 */
export const MOVE_SPEED_BPS_ONE = 10_000;

/**
 * **A lei.** Pura, exata, e sem estado.
 *
 * ## A identidade, e porque ela é estrutural
 *
 * Sem bónus nenhum a função devolve o argumento **pelo mesmo `number`**, sem
 * somar. Não é uma otimização: é a garantia de que um casco sem esta passiva
 * anda exatamente à velocidade a que andava antes deste gate existir, e
 * consegui-lo por construção é diferente de o conseguir por a aritmética calhar
 * devolver o mesmo número.
 *
 * ## Porque o bónus entra em `bigint` e sai em `number`
 *
 * Porque é assim que ele chega: o resolvedor de efeitos mantém tudo em `bigint`
 * por causa das grandezas sem tecto. Esta tem um - a joia declara o seu teto no
 * catálogo - e o total cabe folgado num inteiro seguro, mas a conversão acontece
 * DEPOIS de a soma existir, para que um perfil absurdo seja recusado em vez de
 * perder dígitos em silêncio.
 *
 * ## O que ela recusa
 *
 * Um bónus negativo, que seria uma passiva a tirar velocidade sem que ninguém o
 * tenha desenhado, e um total abaixo de um basis point, que seria uma nave
 * pregada ao sítio onde nasceu. As duas são estados que não deviam poder chegar
 * aqui, e continuar em silêncio seria mover um casco a partir de um número que
 * não devia existir.
 */
export function composeMoveSpeedBps(baseBps: number, summedBonusBps: bigint): number {
  assertSafeInteger(baseBps, 'base movement speed bps');
  if (baseBps < 1) {
    throw new RangeError(`a hull's own movement speed is at least one bps, got ${baseBps}`);
  }
  if (summedBonusBps < 0n) {
    throw new RangeError(`a movement speed bonus never removes speed, got ${summedBonusBps}`);
  }
  if (summedBonusBps === 0n) {
    /* Sem bónus, a velocidade é o MESMO valor. Ver a nota acima. */
    return baseBps;
  }
  const composed = baseBps + Number(summedBonusBps);
  assertSafeInteger(composed, 'composed movement speed bps');
  return composed;
}
